import { FC } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { CMS_LOCALE_NAMES, CMS_LOCALES, CmsPage } from "services/cms-seo/type"

import { RailRow, ScoreChip, StatusBadge } from "../CmsUi"
import { formatDate, formatDateTime } from "../helpers/format"
import { SeoCheckLevel, SeoScoreResult } from "../helpers/seoScore"

const CHECK_ICON: Record<SeoCheckLevel, string> = { ok: "✓", warn: "!", bad: "✕" }
const CHECK_COLOR: Record<SeoCheckLevel, string> = { ok: "text-emerald-500", warn: "text-amber-500", bad: "text-rose-500" }

interface EditorRailProps {
  page: CmsPage
  score: SeoScoreResult
  revalidateSeconds: number
  lastPurgeAt: string | null
  onSwitchLocale: (pageId: string) => void
  onCreateLocale: (locale: string) => void
}

export const EditorRail: FC<EditorRailProps> = ({
  page,
  score,
  revalidateSeconds,
  lastPurgeAt,
  onSwitchLocale,
  onCreateLocale
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  return (
    <aside className="border-t border-black/[0.07] bg-black/[0.02] px-4 py-5 sm:px-5 dark:border-white/[0.08] dark:bg-white/[0.02] lg:w-[296px] lg:flex-none lg:border-t-0 lg:border-l">
      <section className="mb-6">
        <h4 className="mb-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
          {t("cmsSeo.col.status", { defaultValue: "Status" })}
        </h4>
        <RailRow label={t("cmsSeo.rail.state", { defaultValue: "State" })} value={<StatusBadge status={page.status} />} />
        <RailRow label={t("cmsSeo.rail.published", { defaultValue: "Published" })} value={formatDate(page.published_at)} />
        <RailRow label={t("cmsSeo.rail.updated", { defaultValue: "Updated" })} value={formatDateTime(page.updated_at)} />
        <RailRow label={t("cmsSeo.rail.by", { defaultValue: "By" })} value={page.updated_by ?? "—"} />
      </section>

      <section className="mb-6">
        <h4 className="mb-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
          {t("cmsSeo.filter.locale", { defaultValue: "Locale" })}
        </h4>
        {CMS_LOCALES.map((locale) => {
          const sibling = page.translations[locale]

          return (
            <RailRow
              key={locale}
              label={`${CMS_LOCALE_NAMES[locale]} (${locale.toUpperCase()})`}
              value={
                sibling ? (
                  <button
                    type="button"
                    disabled={locale === page.locale}
                    onClick={() => onSwitchLocale(sibling.id)}
                    className={clsx(
                      "text-xs font-medium",
                      sibling.status === "published" ? "text-emerald-500" : "text-amber-500",
                      locale !== page.locale && "underline decoration-dotted underline-offset-2"
                    )}
                  >
                    {sibling.status === "published"
                      ? t("cmsSeo.rail.live", { defaultValue: "Live" })
                      : t("cmsSeo.status.draft", { defaultValue: "Draft" })}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onCreateLocale(locale)}
                    className="text-xs font-medium text-gray-400 underline decoration-dotted underline-offset-2 hover:text-purple-500"
                  >
                    {t("cmsSeo.rail.create", { defaultValue: "Create" })}
                  </button>
                )
              }
            />
          )
        })}
      </section>

      <section className="mb-6">
        <h4 className="mb-2.5 flex items-center justify-between text-xs font-semibold text-gray-500 dark:text-gray-400">
          <span>{t("cmsSeo.rail.seoChecks", { defaultValue: "SEO checks" })}</span>
          <ScoreChip passed={score.passed} total={score.total} />
        </h4>
        {score.checks.map((check) => (
          <div key={check.id} className="flex items-start gap-2.5 py-1 text-xs">
            <span className={clsx("mt-[2px] flex-none text-[11px]", CHECK_COLOR[check.level])}>{CHECK_ICON[check.level]}</span>
            <span className="text-gray-600 dark:text-gray-300">{check.message}</span>
          </div>
        ))}
      </section>

      <section>
        <h4 className="mb-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
          {t("cmsSeo.rail.rendering", { defaultValue: "Rendering" })}
        </h4>
        <RailRow
          label={t("cmsSeo.rail.output", { defaultValue: "Output" })}
          value={t("cmsSeo.rail.serverRendered", { defaultValue: "Server-rendered" })}
        />
        <RailRow label={t("cmsSeo.rail.revalidate", { defaultValue: "Revalidate" })} value={`${revalidateSeconds}s`} />
        <RailRow label={t("cmsSeo.rail.cachePurged", { defaultValue: "Cache purged" })} value={formatDateTime(lastPurgeAt)} />
      </section>
    </aside>
  )
}
